import { createContext, useContext, useState, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';

export interface DemandeClientMemo {
  chantierLabel: string;
  devisDemande: boolean;
  // RC2.74 / RC2.75 : declarations du client sur la ligne de dechet inerte. Sans elles
  // ici, le client saisit une information que personne ne lit cote admin.
  dechetsInertes: Array<{
    nom: string;
    tonnage: number | null;
    precisionDechet: string | null;
    contientHap: boolean;
  }>;
  camionsDemandes: Array<{ nom: string; categorie: string | null }>;
  // D06-60 (RC2.57) : le client a demande un transport sans preciser de type de
  // camion. L'admin doit voir qu'un arbitrage lui revient, pas un bloc vide.
  camionPeuImporte: boolean;
  // RC3.108 : nombre global de camions souhaite sur la demande de devis, absent si non precise.
  nombreCamions?: number;
}

interface DemandeClientMemoContextValue {
  memo: DemandeClientMemo | null;
  open: (data: DemandeClientMemo) => void;
  close: () => void;
}

const DemandeClientMemoContext = createContext<DemandeClientMemoContextValue | null>(null);

export function DemandeClientMemoProvider({ children }: { children: ReactNode }) {
  const [memo, setMemo] = useState<DemandeClientMemo | null>(null);

  const open = useCallback((data: DemandeClientMemo) => setMemo(data), []);
  const close = useCallback(() => setMemo(null), []);

  const value = useMemo(() => ({ memo, open, close }), [memo, open, close]);

  return (
    <DemandeClientMemoContext.Provider value={value}>
      {children}
    </DemandeClientMemoContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- pattern Provider+hook : useDemandeClientMemo est inseparable du Provider. Le warning n'impacte que le HMR de ce fichier.
export function useDemandeClientMemo(): DemandeClientMemoContextValue {
  const ctx = useContext(DemandeClientMemoContext);
  if (!ctx) throw new Error('useDemandeClientMemo must be used within DemandeClientMemoProvider');
  return ctx;
}
